
import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, Database, Wifi, Loader2, RefreshCw, AlertCircle, Briefcase, User as UserIcon, X } from 'lucide-react';
import { User, UserRole, Vacancy, CVData, CVTemplate, Client, VacancyStep } from './types';
import { MOCK_VACANCIES, WERFTOOLS_DEFAULT_TENANT_CLIENT_ID } from './constants';
import { resolveVisibleTabsFromProfile } from './utils/tenantAccess';
import Layout from './components/Layout';
import ClientPortalLayout from './components/ClientPortalLayout';
import Dashboard from './components/Dashboard';
import ClientDashboard from './components/ClientDashboard';
import AdminDashboard from './components/AdminDashboard';
import VacancyIntake from './components/VacancyIntake';
import VacancyTemplates from './components/VacancyTemplates';
import VacancyFlow from './components/VacancyFlow';
import VacancyList from './components/VacancyList';
import VacancyDashboard from './components/VacancyDashboard';
import ClientList from './components/ClientList';
import TalentPool from './components/TalentPool';
import TalentPoolStatistics from './components/TalentPoolStatistics';
import Reporting from './components/Reporting';
import ClientPortalReportingComingSoon from './components/ClientPortalReportingComingSoon';
import ReportingKpiSettings from './components/ReportingKpiSettings';
import ReportingRatios from './components/ReportingRatios';
import AutomationManager from './components/AutomationManager';
import Profile from './components/Profile';
import SubscriptionManager from './components/SubscriptionManager';
import LogsManager from './components/LogsManager';
import ProductCatalog from './components/ProductCatalog';
import RecruiterWorkspace from './components/RecruiterWorkspace';
import UserManagement from './components/UserManagement';
import CareersSiteEditor from './components/CareersSiteEditor';
import WebsiteEditor from './components/WebsiteEditor';
import CVBuilder from './components/CVBuilder';
import CVTemplateManager from './components/CVTemplateManager';
import DocsManager from './components/DocsManager';
import DocumentBuilder from './components/DocumentBuilder';
import DocumentShortcodes from './components/DocumentShortcodes';

import { Login } from './components/Login';
import ClientPortalPreview from './components/ClientPortalPreview';

import Settings from './components/Settings';
import ClientsManager from './components/ClientsManager';
import CRMManager from './components/CRMManager';
import CreateVacancy from './components/CreateVacancy';
import AccountSuccess from './components/AccountSuccess';
import PendingApproval from './components/PendingApproval';
import PublicVacancyView from './components/PublicVacancyView';
import SourcingTool from './components/SourcingTool';
import GlobalPipeline from './components/GlobalPipeline';
import GlobalShortlistTable from './components/GlobalShortlist';
import CandidateEngagementCenter from './components/CandidateEngagementCenter';
import ClientEngagementCenter from './components/ClientEngagementCenter';
import AskFroukjeChat from './components/AskFroukjeChat';
import BullhornIntegration from './components/BullhornIntegration';
import BullhornPipelineSync from './components/BullhornPipelineSync';
import PlacementsList from './components/PlacementsList';
import {
  supabase,
  supabase_get_site_config,
  supabase_get_vacancies,
  supabase_get_vacancy_by_id,
  supabase_get_clients,
  supabase_get_paginated_talent_pool,
  supabase_get_portal_scoped_candidate_ids,
  supabase_get_talent_pool,
  supabase_search_talent_pool,
  supabase_get_client_branding,
  extractErrorMessage,
  supabase_insert_vacancy,
  supabase_get_session_user_email,
  supabase_safe_sign_out,
} from './services/supabaseService';
import { sendUserInvite } from './services/notificationService';
import TodoWidget from './components/TodoWidget';
import GlobalLoader from './components/GlobalLoader';
import { CallSidebar } from './components/CallSidebar';
import { sidebarGroupIdForTab } from './sidebarNav';
import { RESUME_VOORGESTELD_MOVE_MODAL_EVENT } from './utils/voorgesteldResumeNavigation';
import {
  mergeVacancyPreserveHeavyContent,
  formatVacancyRowFromSupabase,
} from './utils/vacancyIntakeVersions';
import { isPortalArchivedVacancy, resolvePortalFullPipelineVisibility } from './utils/clientPortalGuards';

// ---------------------------------------------------------------------------
// URL SLUG ROUTING
// Maps internal tab names to URL slugs under /app/
// ---------------------------------------------------------------------------
const TAB_TO_SLUG: Record<string, string> = {
  dashboard: 'dashboard',
  pipeline: 'pipeline',
  shortlist: 'shortlist',
  workspace: 'workspace',
  vacancies: 'vacancies',
  create_vacancy: 'vacancies/new',
  talentpool: 'talentpool',
  crm: 'crm',
  clients: 'clients',
  cv_builder: 'cv-builder',
  cv_templates: 'cv-templates',
  docs: 'docs',
  documentbuilder: 'documentbuilder',
  doc_shortcodes: 'docs/shortcodes',
  bullhorn_pipeline: 'bullhorn-pipeline',
  careers_site: 'careers-site',
  automation: 'automation',
  sourcing: 'sourcing',
  engagement_center: 'candidate-engagement',
  reporting: 'reporting',
  reporting_kpi: 'reporting-kpi',
  reporting_ratios: 'reporting-ratios',
  settings: 'settings',
  users: 'users',
  subscription: 'subscription',
  logs: 'logs',
  products: 'products',
  vacancy_templates: 'vacancy-templates',
  account_success: 'account-success',
};

const SLUG_TO_TAB: Record<string, string> = {
  ...Object.fromEntries(Object.entries(TAB_TO_SLUG).map(([tab, slug]) => [slug, tab])),
  /** Alias voor clientportaal / bookmarks (naast /app/candidate-engagement). */
  'engagement-center': 'engagement_center',
};

/** Parse the current pathname and return { tab, vacancyId, candidateId } */
const parseCurrentPath = () => {
  const segments = window.location.pathname.replace(/^\//, '').split('/');
  // Expect: app / <slug> / [id]
  if (segments[0] !== 'app') return { tab: 'dashboard', vacancyId: null, candidateId: null };
  const slug = segments[1] || 'dashboard';
  const id = segments[2] || null;
  const tab = SLUG_TO_TAB[slug] || SLUG_TO_TAB[`${slug}/${id}`] || slug;
  const vacancyId = (slug === 'vacancies' && id && id !== 'new') ? id : null;
  const candidateId = slug === 'talentpool' ? id : null;
  return { tab, vacancyId, candidateId };
};

const App: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const isFetchingProfile = React.useRef(false); // Guard against concurrent fetchProfile calls

  // Initialize tab from URL slug (supports page refresh & direct links)
  const _initial = parseCurrentPath();
  const isClientPortalBoot =
    typeof window !== 'undefined' &&
    (window.location.hostname.includes('clientportal') || window.location.href.includes('clientportal=true'));
  const tabFromPath =
    isClientPortalBoot && (_initial.tab === 'dashboard' || !_initial.tab || _initial.tab === '')
      ? 'engagement_center'
      : _initial.tab;
  const initialHash = window.location.hash;
  const initialTab = (initialHash.includes('documentbuilder') || initialHash.includes('code='))
    ? 'documentbuilder'
    : tabFromPath;
  const [activeTab, setActiveTabRaw] = useState(initialTab);
  const [engagementCockpitLayout, setEngagementCockpitLayout] = useState(false);
  
  const [selectedVacancy, setSelectedVacancy] = useState<Vacancy | null>(null);
  const [isEditingVacancy, setIsEditingVacancy] = useState(false);

  // CV Builder State
  const [cvBuilderData, setCvBuilderData] = useState<CVData | null>(null);
  const [cvBuilderTemplate, setCvBuilderTemplate] = useState<CVTemplate | undefined>(undefined);
  const [cvBuilderReturn, setCvBuilderReturn] = useState<{
    tab: string;
    vacancyId?: string;
    portalProposal?: boolean;
  } | null>(null);
  const [builderSessionId, setBuilderSessionId] = useState<number>(0);

  // Docs State
  const [docsData, setDocsData] = useState<{ title: string, content: string, candidateId?: string, fileId?: string } | null>(null);
  const [documentBuilderData, setDocumentBuilderData] = useState<{
    title: string;
    content: string;
    candidateId?: string;
    fileId?: string;
    vacancyId?: string;
    vacancyTitle?: string;
  } | null>(null);
  const [documentBuilderReturn, setDocumentBuilderReturn] = useState<{
    tab: string;
    vacancyId?: string;
    applicationId?: string;
    portalProposal?: boolean;
  } | null>(null);
  // Persistent state for TalentPool selection (seed from URL on load)
  const [lastTalentPoolCandidateId, setLastTalentPoolCandidateId] = useState<string | null>(_initial.candidateId);
  const [lastClientId, setLastClientId] = useState<string | null>(null);
  const [lastCrmEntityId, setLastCrmEntityId] = useState<string | null>(null);
  // Resolve a pending vacancy ID (from URL) against liveVacancies once data loads
  const [pendingVacancyId, setPendingVacancyId] = useState<string | null>(_initial.vacancyId);

  // Simple alias — no useCallback needed (avoids hook-order violations)
  const setActiveTab = setActiveTabRaw;

  const onEngagementCockpitLayout = useCallback((v: boolean) => {
    setEngagementCockpitLayout(v);
  }, []);

  // Global Call State
  const [showCallModal, setShowCallModal] = useState(false);
  const [activeCallCandidate, setActiveCallCandidate] = useState<any | null>(null);
  const [activeCallProfileId, setActiveCallProfileId] = useState<string | null>(null);
  const [editCallCommId, setEditCallCommId] = useState<string | undefined>(undefined);
  const [editCallContent, setEditCallContent] = useState<string | undefined>(undefined);

  const [isProcessing, setIsProcessing] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [isCmsActive, setIsCmsActive] = useState(false);
  const [config, setConfig] = useState<any>(null);
  const [siteConfig, setSiteConfig] = useState<any>(null);
  const [showTodoWidget, setShowTodoWidget] = useState(false);
  const [showFroukjeChat, setShowFroukjeChat] = useState(false);
  const [isAdminAlertSent, setIsAdminAlertSent] = useState(false);

  // --- TASKBAR STATE ---
  const [taskbarItems, setTaskbarItems] = useState<Array<{ id: string, type: 'vacancy' | 'candidate', title: string }>>([]);
  const [activeTaskbarId, setActiveTaskbarId] = useState<string | null>(null);

  // Loading Safety
  const [isLongLoading, setIsLongLoading] = useState(false);

  // --- LIVE DATA STATE ---
  const [liveVacancies, setLiveVacancies] = useState<Vacancy[]>([]);
  const [liveClients, setLiveClients] = useState<Client[]>([]);
  const [liveCandidates, setLiveCandidates] = useState<any[]>([]);
  /** Klantportaal: eigen client + werkplekken (parent_client_id) — zelfde set als supabase_get_vacancies. */
  const [portalAllowedClientIds, setPortalAllowedClientIds] = useState<string[]>([]);
  const [isLiveConnected, setIsLiveConnected] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState<boolean | number>(false);

  // --- PUBLIC CAREER SITE STATE ---
  const [publicClientData, setPublicClientData] = useState<{ client: Client, branding: any } | null>(null);

  // --- CREATE VACANCY STATE ---
  const [createVacancyInitialData, setCreateVacancyInitialData] = useState<any>(null);
  const [isClientPortalDomain] = useState(() => window.location.hostname.includes('clientportal') || window.location.href.includes('clientportal=true'));

  const [toastData, setToastData] = useState<{ message: string; type: 'error' | 'success' | 'info'; sticky?: boolean } | null>(null);

  useEffect(() => {
    const handleToast = (e: any) => {
      const detail = e.detail || {};
      setToastData(detail);
      if (!detail?.sticky) {
        setTimeout(() => setToastData(null), 4000);
      }
    };
    window.addEventListener('wt:toast', handleToast);
    return () => window.removeEventListener('wt:toast', handleToast);
  }, []);

  useEffect(() => {
    if (!isClientPortalDomain || !user?.clientId) {
      setPortalAllowedClientIds([]);
      return;
    }
    setPortalAllowedClientIds([String(user.clientId)]);
  }, [isClientPortalDomain, user?.clientId]);

  // ---------------------------------------------------------------------------
  // URL SYNC: push slug to browser history whenever active tab or entity changes
  // ---------------------------------------------------------------------------

  // Sync URL whenever tab / vacancy / candidateId changes
  useEffect(() => {
    // GUARD: If this is an OAuth callback (Azure/SSO), DO NOT sync URL yet. Let Supabase process the code.
    if (window.location.search.includes('code=') || window.location.hash.includes('access_token=')) {
      console.log("[App] URL Sync skipped (OAuth callback detected in URL).");
      return;
    }

    const slug = TAB_TO_SLUG[activeTab] || activeTab;
    let path = `/app/${slug}`;
    if (activeTab === 'vacancies' && selectedVacancy?.id) {
      path = `/app/vacancies/${selectedVacancy.id}`;
    } else if (activeTab === 'talentpool' && lastTalentPoolCandidateId) {
      path = `/app/talentpool/${lastTalentPoolCandidateId}`;
    }
    if (window.location.pathname !== path) {
      window.history.pushState({ tab: activeTab, path }, '', path);
    }
  }, [activeTab, selectedVacancy?.id, lastTalentPoolCandidateId]);

  // Handle browser Back / Forward
  useEffect(() => {
    const onPopState = () => {
      const { tab, vacancyId, candidateId } = parseCurrentPath();
      setActiveTabRaw(tab);
      if (vacancyId) {
        setSelectedVacancy(null);
        setPendingVacancyId(vacancyId);
      } else {
        setSelectedVacancy(null);
      }
      if (candidateId) setLastTalentPoolCandidateId(candidateId);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // Global navigation event — allows any child component to navigate via:
  // window.dispatchEvent(new CustomEvent('wt:navigate', { detail: 'settings' }))
  useEffect(() => {
    const onNavigate = (e: Event) => {
      const d = (e as CustomEvent).detail as any;
      if (d && typeof d === 'object' && d.tab === 'cv_builder') {
        setCvBuilderData(d.data ?? null);
        setCvBuilderTemplate(d.template);
        setCvBuilderReturn(d.cvBuilderReturn ?? null);
        setBuilderSessionId(Date.now());
        setActiveTab('cv_builder');
        return;
      }
      if (d && typeof d === 'object' && d.tab === 'documentbuilder') {
        if (d.docContent) setDocumentBuilderData(d.docContent);
        else if (d.data) setDocumentBuilderData(d.data);
        setDocumentBuilderReturn(d.documentBuilderReturn ?? null);
        setActiveTab('documentbuilder');
        return;
      }
      let tab = typeof d === 'string' ? d : d?.tab;
      if (isClientPortalDomain && tab === 'dashboard') tab = 'engagement_center';
      if (tab) setActiveTab(tab);
    };
    window.addEventListener('wt:navigate', onNavigate);
    return () => window.removeEventListener('wt:navigate', onNavigate);
  }, [isClientPortalDomain]);

  useEffect(() => {
    const handleLocationChange = async () => {
      // PATH-BASED ROUTING: Check pathname first (e.g. /CompanyName)
      const path = window.location.pathname.substring(1); // Remove leading slash

      // Ignore reserved paths or empty path (Home)
      if (!path || path === 'login' || path === 'admin' || path.startsWith('app')) {
        setPublicClientData(null);
        return;
      }

      if (path && path.length > 2) {
        try {
          // Fetch all clients to find match
          const allClients = await supabase_get_clients();
          // Match by ID or Name (Slugified)
          const matchedClient = allClients?.find(c =>
            c.id === path ||
            c.name.toLowerCase().replace(/\s+/g, '') === path.toLowerCase() ||
            c.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') === path.toLowerCase()
          );

          if (matchedClient) {
            const branding = await supabase_get_client_branding(matchedClient.id);
            setPublicClientData({ client: matchedClient, branding });
          } else {
            // If valid path but no client found, maybe redirect to home or show 404? 
            // For now, let it fall through to App (Landing Page) as "not found"
            setPublicClientData(null);
          }
        } catch (e) { console.error("Error loading public site:", e); setPublicClientData(null); }
      } else {
        setPublicClientData(null);
      }
    };

    handleLocationChange();
    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, []);

  // OAuth / magic-link error in URL (e.g. failed profile trigger on new Supabase project)
  useEffect(() => {
    const search = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const authError = search.get('error') || hash.get('error');
    if (authError) {
      const desc = search.get('error_description') || hash.get('error_description');
      const msg = desc ? decodeURIComponent(desc.replace(/\+/g, ' ')) : authError;
      console.error('[App] Auth redirect error:', msg);
      window.history.replaceState({}, '', window.location.pathname || '/');
      alert(
        `Inloggen mislukt: ${msg}\n\n` +
          'Op een nieuwe Supabase-database: voer sql/setup_new_supabase_auth.sql uit in de SQL Editor, ' +
          'verwijder mislukte users onder Authentication → Users, en probeer opnieuw.'
      );
    }
  }, []);

  // Initial Session Check
  useEffect(() => {
    let mounted = true;

    // Safety timer: If loading takes > 3s, show the cancel option
    const safetyTimer = setTimeout(() => {
      if (mounted && isProcessing) {
        setIsLongLoading(true);
      }
    }, 3000);

    const checkSession = async () => {
      // Skip session check for public routes to prevent timeouts for anonymous users
      const path = window.location.pathname.substring(1);
      const isPublicRoute = path && path.length > 2 && !['login', 'admin', 'dashboard'].includes(path) && !path.startsWith('app');

      if (isPublicRoute) {
        if (mounted) setIsProcessing(false);
        return;
      }

      try {
        console.log("[App] checkSession: starting...");
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) throw error;

        if (session && mounted) {
          console.log("[App] checkSession: session found, user:", session.user.id);
          await fetchProfile(session.user.id);
        } else {
          console.log("[App] checkSession: no session found");
          if (mounted) setIsProcessing(false);
        }
      } catch (e) {
        console.warn("Session check warning:", e);
        if (mounted) setIsProcessing(false);
      }
    };

    checkSession();

    const fetchSite = async () => {
      try {
        const conf = await supabase_get_site_config();
        if (conf && mounted) {
           setConfig(conf);
           setSiteConfig(conf);
        }
      } catch (e) { console.warn("Site config skipped"); }
    };
    fetchSite();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("[App] onAuthStateChange event:", event);
      if (!mounted) return;
      if (event === 'SIGNED_IN' && session) {
        console.log("[App] onAuthStateChange: SIGNED_IN for user:", session.user.id);
        fetchProfile(session.user.id, session.user.email); // Uncommented to fix Azure OAuth PKCE race
      } else if (event === 'SIGNED_OUT') {
        console.log("[App] onAuthStateChange: SIGNED_OUT");
        setUser(null);
        setActiveTab('dashboard');
        setSelectedVacancy(null);
        setIsEditingVacancy(false);
        setIsCmsActive(false);
        setShowLogin(false);
        setLiveVacancies([]);
        setLiveClients([]);
        setLiveCandidates([]);
        setIsLiveConnected(false);
        setIsLoadingData(false);
        setIsProcessing(false);
        setIsAdminAlertSent(false); // Reset alert status on sign out
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
      clearTimeout(safetyTimer);
    };
  }, []);

  // --- AUTO REFRESH ON FOCUS & CUSTOM EVENTS ---
  useEffect(() => {
    const handleFocus = () => {
      if (user) refreshAllData(true); // Silent refresh
    };

    const handleNavigateTab = (e: any) => {
      if (e.detail) {
        setActiveTab(e.detail);
      }
    };

    const handleOpenCandidate = (e: any) => {
      if (e.detail) {
        setLastTalentPoolCandidateId(e.detail);
        setActiveTab('talentpool');
      }
    };

    const handleOpenVacancy = (e: any) => {
      if (e.detail) {
        setPendingVacancyId(e.detail);
        setActiveTab('vacancies');
      }
    };

    const handleOpenClient = (e: any) => {
        if (e.detail) {
            setLastCrmEntityId(e.detail);
            setActiveTab('crm');
        }
    };

    const handleTaskbarAdd = (e: any) => {
       const item = e.detail;
       if (item && item.id) {
          setTaskbarItems(prev => {
             const exists = prev.find(i => i.id === item.id);
             let next = exists ? prev.map(i => i.id === item.id ? { ...i, title: item.title } : i) : [...prev, item];
             if (next.length > 10) {
                 next = next.slice(next.length - 10); // Keep the 10 most recent
             }
             return next;
          });
          setActiveTaskbarId(item.id);
       }
    };

    // window.addEventListener('focus', handleFocus);
    window.addEventListener('navigate-tab', handleNavigateTab);
    window.addEventListener('open-candidate', handleOpenCandidate);
    window.addEventListener('open-vacancy', handleOpenVacancy);
    window.addEventListener('open-client', handleOpenClient);
    window.addEventListener('wt:taskbar-add', handleTaskbarAdd);
    return () => {
      // window.removeEventListener('focus', handleFocus);
      window.removeEventListener('navigate-tab', handleNavigateTab);
      window.removeEventListener('open-candidate', handleOpenCandidate);
      window.removeEventListener('open-vacancy', handleOpenVacancy);
      window.removeEventListener('open-client', handleOpenClient);
      window.removeEventListener('wt:taskbar-add', handleTaskbarAdd);
    };
  }, [user]);

  // Update active taskbar id when selectedVacancy changes
  useEffect(() => {
     if (selectedVacancy && activeTab === 'vacancies') {
        setActiveTaskbarId(selectedVacancy.id);
        window.dispatchEvent(new CustomEvent('wt:taskbar-add', { detail: { id: selectedVacancy.id, type: 'vacancy', title: selectedVacancy.title } }));
     }
  }, [selectedVacancy, activeTab]);

  // Update active taskbar id when talentpool candidate is active
  useEffect(() => {
     if (activeTab === 'talentpool' && lastTalentPoolCandidateId) {
        setActiveTaskbarId(lastTalentPoolCandidateId);
        const cand = liveCandidates.find(c => c.id === lastTalentPoolCandidateId || c.candidate_id === lastTalentPoolCandidateId);
        if (cand) {
           const title = cand.name || cand.candidate?.name || 'Kandidaat';
           window.dispatchEvent(new CustomEvent('wt:taskbar-add', { detail: { id: lastTalentPoolCandidateId, type: 'candidate', title } }));
        }
     }
  }, [activeTab, lastTalentPoolCandidateId, liveCandidates]);

  // Update active taskbar id when CRM client is active
  useEffect(() => {
     if (activeTab === 'crm' && lastCrmEntityId) {
        setActiveTaskbarId(lastCrmEntityId);
        const client = liveClients.find(c => c.id === lastCrmEntityId);
        const title = client?.name || 'Relatie Profiel';
        window.dispatchEvent(new CustomEvent('wt:taskbar-add', { detail: { id: lastCrmEntityId, type: 'crm', title } }));
     }
  }, [activeTab, lastCrmEntityId, liveClients]);

  const handleTaskbarClick = (item: any) => {
     setActiveTaskbarId(item.id);
     if (item.type === 'vacancy') {
        window.dispatchEvent(new CustomEvent('open-vacancy', { detail: item.id }));
     } else if (item.type === 'candidate') {
        window.dispatchEvent(new CustomEvent('open-candidate', { detail: item.id }));
     } else if (item.type === 'crm') {
        window.dispatchEvent(new CustomEvent('open-client', { detail: item.id }));
     } else if (item.type === 'placement') {
        window.dispatchEvent(new CustomEvent('wt:navigate', { detail: 'placements' }));
        setTimeout(() => {
           window.dispatchEvent(new CustomEvent('open-placement', { detail: item.id }));
        }, 100);
     }
  };

  const removeTaskbarItem = (id: string, e: React.MouseEvent) => {
     e.stopPropagation();
     setTaskbarItems(prev => {
        const next = prev.filter(i => i.id !== id);
        if (activeTaskbarId === id) {
           if (next.length > 0) {
              handleTaskbarClick(next[next.length-1]);
           } else {
              window.dispatchEvent(new CustomEvent('wt:navigate', { detail: isClientPortalDomain ? 'engagement_center' : 'dashboard' }));
           }
        }
        return next;
     });
  };

  // --- FORCE GLOBAL LOGOUT AND REFRESH SCRIPT ---
  // Dit zorgt ervoor dat iedereen die de nieuwe versie opent, één keer wordt uitgelogd en gerefresht.
  useEffect(() => {
    const versionKey = 'werftools_force_logout_v2';
    if (!localStorage.getItem(versionKey)) {
      console.warn("Force logout triggered due to version update.");
      localStorage.clear();
      localStorage.setItem(versionKey, 'true');
      supabase.auth.signOut().then(() => {
        window.location.href = '/login'; // Or root
      });
    }
  }, []);

  // --- REFS FOR STABLE CALLBACKS ---
  const userRef = React.useRef<User | null>(null);
  const refreshInFlight = React.useRef(false);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // --- REALTIME DATA SYNC ---
  useEffect(() => {
    if (user?.id) {
      // 1. Initial Data Load
      refreshAllData();

      // 2. Setup Realtime Subscription
      let vacTimeout: any;
      let clientTimeout: any;
      let poolTimeout: any;

      const channel = supabase.channel(`db-changes-${user.id}`)
        .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'vacancies' }, (payload) => {
           setLiveVacancies(prev => prev.map(v => {
              if (v.id === payload.new.id) {
                  return { 
                      ...v, 
                      ...payload.new,
                      clientId: payload.new.client_id || v.clientId, 
                      data: { ...v.data, ...(payload.new.data || {}) } 
                  };
              }
              return v;
           }));
           // If selectedVacancy is the one being updated, update it too
           setSelectedVacancy(prev => {
              if (prev && prev.id === payload.new.id) {
                  return mergeVacancyPreserveHeavyContent(
                      {
                          ...prev,
                          clientId: payload.new.client_id || prev.clientId,
                      } as Record<string, unknown>,
                      {
                          ...payload.new,
                          clientId: payload.new.client_id || prev.clientId,
                          data: { ...(payload.new.data || {}) },
                      } as Record<string, unknown>
                  ) as Vacancy;
              }
              return prev;
           });
        })
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'vacancies' }, () => {
          clearTimeout(vacTimeout);
          vacTimeout = setTimeout(() => {
            const u = userRef.current;
            const filterId =
              (isClientPortalDomain && u?.clientId ? String(u.clientId) : undefined) ||
              (u?.role === UserRole.CLIENT || u?.role === UserRole.CLIENT_RECRUITER ? u.clientId : undefined);
            refreshVacancies(filterId);
          }, 2000);
        })
        .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'vacancies' }, (payload) => {
           setLiveVacancies(prev => prev.filter(v => v.id !== payload.old.id));
        })
        .on('postgres_changes', { event: '*', schema: 'public', table: 'clients' }, () => {
          clearTimeout(clientTimeout);
          clientTimeout = setTimeout(() => {
            const u = userRef.current;
            const filterId =
              (isClientPortalDomain && u?.clientId ? String(u.clientId) : undefined) ||
              (u?.role === UserRole.CLIENT || u?.role === UserRole.CLIENT_RECRUITER ? u.clientId : undefined);
            refreshClients(filterId);
          }, 2000);
        })
        .on('postgres_changes', { event: '*', schema: 'public', table: 'candidates' }, () => {
          clearTimeout(poolTimeout);
          poolTimeout = setTimeout(() => {
            const u = userRef.current;
            const filterId =
              (isClientPortalDomain && u?.clientId ? String(u.clientId) : undefined) ||
              (u?.role === UserRole.CLIENT || u?.role === UserRole.CLIENT_RECRUITER ? u.clientId : undefined);
            refreshTalentPool(filterId);
          }, 2000);
        })
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') setIsLiveConnected(true);
          else setIsLiveConnected(false);
        });

      return () => { supabase.removeChannel(channel); };
    }
  }, [user?.id, user?.role, user?.clientId]); // ✅ Stable dependencies

  // Prevent infinite spinner for data loading
  useEffect(() => {
    let safetyTimer: any;
    if (isLoadingData) {
      safetyTimer = setTimeout(() => {
        if (isLoadingData) {
          console.warn("Data load timeout - forcing stop");
          setIsLoadingData(false);
          refreshInFlight.current = false;
        }
      }, 120000); // 120 seconds safety valve
    }
    return () => clearTimeout(safetyTimer);
  }, [isLoadingData]);

  // Resolve pendingVacancyId once liveVacancies is available (from URL on page load/refresh)
  useEffect(() => {
    if (pendingVacancyId && liveVacancies.length > 0) {
      const found = liveVacancies.find(v => v.id === pendingVacancyId);
      if (found) {
        // Fetch full vacancy because liveVacancies are 'light' and missing description/HTML,
        // which causes VacancyFlow autosave to wipe them!
        setIsLoadingData(true);
        import('./services/supabaseService').then(({ supabase_get_vacancy_by_id }) => {
            supabase_get_vacancy_by_id(found.id).then((fullVac) => {
                if (fullVac) {
                    const formattedFullVac = formatVacancyRowFromSupabase(fullVac as Record<string, unknown>);
                    setSelectedVacancy(formattedFullVac as Vacancy);
                } else {
                    setSelectedVacancy(found);
                }
                setActiveTabRaw('vacancies');
                setIsLoadingData(false);
            }).catch(() => {
                setSelectedVacancy(found);
                setActiveTabRaw('vacancies');
                setIsLoadingData(false);
            });
        });
      }
      setPendingVacancyId(null);
    }
  }, [pendingVacancyId, liveVacancies]);

  const refreshAllData = async (silent = false) => {
    // Prevent overlapping refreshes
    if (refreshInFlight.current && !silent) return;

    refreshInFlight.current = true;
    if (!silent) {
      setIsLoadingData(true);
      // We purposefully DO NOT show the Global Loader here anymore.
      // Eager rendering is heavily preferred. The UI will indicate sync via silent 'Live' blobs.
    }

    try {
      const u = userRef.current; // Use ref for latest state
      const portalScope =
        isClientPortalDomain && u?.clientId ? String(u.clientId) : undefined;
      const filterId =
        portalScope ||
        (u?.role === UserRole.CLIENT || u?.role === UserRole.CLIENT_RECRUITER ? u?.clientId : undefined);

      // Run fetches in parallel with a timeout race
      const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 120000));

      await Promise.race([
        (async () => {
          await refreshVacancies(filterId, (pct) => {
            if (!silent) setIsLoadingData(pct === 100 ? true : pct);
          });
          // Vacatures eerst (zwaarste query); daarna pool + clients om DB-timeout te vermijden.
          await refreshClients(filterId);
          await refreshTalentPool(filterId);
          if (!silent) setIsLoadingData(false);
        })(),
        timeout
      ]);

    } catch (e) {
      console.warn("Data refresh issue (or timeout):", e);
    } finally {
      refreshInFlight.current = false;
      setIsLoadingData(false);
    }
  };

  const refreshVacancies = async (filterId?: string, onProgress?: (pct: number) => void) => {
    try {
      if (isClientPortalDomain) {
        const u = userRef.current;
        console.groupCollapsed('[ClientportalVacatures] App refreshVacancies');
        console.info('[ClientportalVacatures] Start', {
          filterId: filterId ?? null,
          userClientId: u?.clientId ?? null,
          userId: u?.id ?? null,
          userEmail: u?.email ?? null,
          userRole: u?.role ?? null,
          clientName: u?.clientName ?? null,
          timestampIso: new Date().toISOString(),
        });
      }

      const data = await supabase_get_vacancies(filterId, onProgress, isClientPortalDomain);
      if (data) {
        let rawData = data as any[];
        const u = userRef.current;
        if (isClientPortalDomain) {
          console.info('[ClientportalVacatures] Ruwe response na supabase_get_vacancies', {
            aantal: rawData?.length ?? 0,
            filterId: filterId ?? null,
          });
        }
        if (isClientPortalDomain && (!rawData || rawData.length === 0)) {
          console.warn('[ClientportalVacatures] 0 rijen van API — mogelijke oorzaken: RLS, verkeerde org-UUID, of geen kolom-match (eindklant/opdrachtgever/client).', {
            filterId,
            userId: u?.id,
            userClientId: u?.clientId,
          });
        }
        if (isClientPortalDomain && u?.clientId) {
          const beforePortalFilter = rawData.length;
          const filterStats = {
            totaalIn: beforePortalFilter,
            uitAfgewezenIngetrokken: 0,
            door: 0,
          };

          rawData = rawData.filter((v) => {
                if (isPortalArchivedVacancy(v)) {
                  filterStats.uitAfgewezenIngetrokken += 1;
                  return false;
                }
                let s = v.status || 'Concept';
                const l = String(s).toLowerCase();
                if (['afgewezen', 'ingetrokken'].includes(l)) {
                  filterStats.uitAfgewezenIngetrokken += 1;
                  return false;
                }
                filterStats.door += 1;
                return true;
            });
          console.info('[ClientportalVacatures] Na client-side filter', {
            ...filterStats,
            uniekNaDedup: new Map(rawData.map((v: any) => [v.id, v])).size,
          });
          if (beforePortalFilter > 0 && rawData.length === 0) {
            console.warn(
              '[ClientportalVacatures] Alle ruwe vacatures weggefilterd — alleen status afgewezen/ingetrokken zou dit moeten doen.',
              { portalClientId: u.clientId }
            );
          }
        }

        const formatted = rawData.map((v: any) => ({
          ...v,
          _isLight: true,
          clientId: v.client_id,
          createdAt: new Date(v.created_at).toLocaleDateString('nl-NL'),
          client: v.clients,
          data: { ...v.data, tags: v.data?.tags || [], category: v.data?.category || '' }
        }));
        const uniqueFormatted = Array.from(new Map(formatted.map(v => [v.id, v])).values());
        setLiveVacancies(uniqueFormatted);

        if (isClientPortalDomain) {
          console.info('[ClientportalVacatures] State liveVacancies gezet', {
            aantalUniek: uniqueFormatted.length,
            ids: uniqueFormatted.slice(0, 12).map((v: any) => v.id),
          });
          console.groupEnd();
        }
        
        if (selectedVacancy) {
           const updated = formatted.find(v => v.id === selectedVacancy.id);
           if (updated) {
             setSelectedVacancy((prev) =>
               prev?.id === updated.id
                 ? (mergeVacancyPreserveHeavyContent(prev as Record<string, unknown>, updated as Record<string, unknown>) as Vacancy)
                 : prev
             );
           }
        }
      } else if (isClientPortalDomain) {
        console.warn('[ClientportalVacatures] supabase_get_vacancies gaf null/undefined terug');
        console.groupEnd();
      }
    } catch (error) {
      if (isClientPortalDomain) {
        console.error('[ClientportalVacatures] refreshVacancies fout', error);
        try {
          console.groupEnd();
        } catch {
          /* */
        }
      } else {
        console.error("Vacancies fetch error", error);
      }
    }
  };

  const refreshClients = async (filterId?: string) => {
    try {
      const data = await supabase_get_clients(filterId);
      if (data) setLiveClients(data);
    } catch (error) { console.error("Clients fetch error", error); }
  };

  const refreshTalentPool = async (filterId?: string) => {
    try {
      const u = userRef.current;
      const portalScope =
        isClientPortalDomain && u?.clientId ? String(u.clientId) : undefined;
      const activeFilter =
        portalScope ||
        filterId ||
        ((u?.role === UserRole.CLIENT || u?.role === UserRole.CLIENT_RECRUITER) ? u?.clientId : undefined);

      let poolFilters: Record<string, unknown> | undefined;
      if (portalScope) {
        const scopedIds = await supabase_get_portal_scoped_candidate_ids(portalScope);
        poolFilters = { scopedCandidateIds: scopedIds };
      }

      const res = await supabase_get_paginated_talent_pool(
        activeFilter,
        1,
        50,
        '',
        'created_at',
        false,
        false,
        poolFilters,
      );
      if (res) setLiveCandidates(res.data);
    } catch (error) { console.error("Pool fetch error", error); }
  };

  const fetchProfile = async (uid: string, sessionEmailHint?: string) => {
    // Guard: Prevent duplicate fetch if user data is already fully loaded
    if (userRef.current?.id === uid) {
      console.log("[App] fetchProfile: user already loaded, skipping. uid:", uid);
      return;
    }

    if (isFetchingProfile.current) {
      console.log("[App] fetchProfile: already in progress, skipping duplicate call for", uid);
      return;
    }
    isFetchingProfile.current = true;
    console.log("[App] fetching profile for", uid);
    setIsProcessing(true);
    try {

      // 45s timeout -> Reduced to 10s for debugging
      const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('Profile timeout (10s)')), 10000));

      console.log("[App] starting supabase query (no join)...");
      const profilePromise = supabase
        .from('profiles')
        .select('*') // REMOVED clients(name) join to prevent RLS recursion/deadlocks
        .eq('id', uid)
        .maybeSingle();

      let profileResult;
      try {
        profileResult = await Promise.race([profilePromise, timeout]) as { data: any, error: any };
      } catch (raceErr) {
        console.warn("[App] Profile RACE timeout/error:", raceErr);
        // Fallback: If timeout, don't crash. Just execute minimal.
        profileResult = { data: null, error: null };
      }

      const { data: profile, error } = profileResult;
      console.log("[App] profile query result:", { profile, error });

      let authSessionEmail = String(sessionEmailHint || '').trim();
      if (!authSessionEmail) {
        authSessionEmail = await supabase_get_session_user_email();
      }

      if (error) {
        console.error("Profile DB Error:", error);
        // Don't throw, just warn.
      }

      if (profile) {
        // Klantportaal: CRM-match op e-mail — profiles.email is soms leeg; auth.users wel prom@… .nl
        const em = String(profile.email || authSessionEmail || '').trim();
        let effectiveClientId = profile.client_id;
        let effectiveClientName = 'Mijn Organisatie'; // Default

        const tryResolveClientFromCrmContact = async (): Promise<string | null> => {
          let resolved: string | null = null;
          let portalOk = true;
          const emailVariants = Array.from(
            new Set(
              [em, em.replace(/\.nl$/i, '')].filter((x) => String(x || '').trim().length > 0)
            )
          ) as string[];
          try {
            for (const variant of emailVariants) {
              const { data: rpcCid, error: rpcErr } = await supabase.rpc('resolve_client_id_for_portal_email', {
                check_email: variant,
              });
              if (!rpcErr && rpcCid) {
                resolved = String(rpcCid);
                break;
              }
              if (rpcErr) console.warn('[App] resolve_client_id_for_portal_email:', rpcErr, 'email=', variant);
            }
            if (!resolved) {
              for (const variant of emailVariants) {
                const { data: ccRows, error: ccErr } = await supabase
                  .from('client_contacts')
                  .select('client_id, linked_client_id, active_clientportal')
                  .ilike('email', variant)
                  .order('created_at', { ascending: false })
                  .limit(1);
                if (ccErr) console.warn('[App] client_contacts lookup for portal heal failed:', ccErr);
                const cc = ccRows?.[0];
                const orgFromContact =
                  (cc as any)?.linked_client_id ||
                  ((cc as any)?.client_id && String((cc as any).client_id) !== WERFTOOLS_DEFAULT_TENANT_CLIENT_ID
                    ? String((cc as any).client_id)
                    : null);
                if (orgFromContact) {
                  resolved = String(orgFromContact);
                  portalOk = cc.active_clientportal !== false;
                  break;
                }
              }
            }
          } catch (e) {
            console.warn('[App] portal heal from client_contacts failed:', e);
          }
          return resolved && portalOk ? resolved : null;
        };

        // Zorg dat profiel hetzelfde adres heeft als login (helpt RPC + RLS-functies die profiles.email lezen).
        if (authSessionEmail && !String(profile.email || '').trim()) {
          try {
            await supabase.from('profiles').update({ email: authSessionEmail }).eq('id', uid);
          } catch {
            /* non-fatal */
          }
        }

        // Klantportaal (alle rollen met e-mail): organisatie via CRM-contact, daarna portal-uitnodiging.
        if (isClientPortalDomain && em) {
          const crmId = await tryResolveClientFromCrmContact();
          if (crmId) {
            effectiveClientId = crmId;
            if (profile.client_id !== crmId) {
              const { error: portalProfErr } = await supabase
                .from('profiles')
                .update({ client_id: crmId })
                .eq('id', uid);
              if (portalProfErr) {
                console.warn('[App] portal: profiles.client_id sync failed (RLS?):', portalProfErr);
              }
            }
          }
          if (!effectiveClientId) {
            try {
              const { data: invite } = await supabase
                .from('client_portal_invites')
                .select('client_id')
                .ilike('email', em)
                .maybeSingle();
              if (invite && invite.client_id) {
                effectiveClientId = invite.client_id;
                if (profile.client_id !== invite.client_id) {
                  const { error: invErr } = await supabase
                    .from('profiles')
                    .update({ client_id: invite.client_id })
                    .eq('id', uid);
                  if (invErr) console.warn('[App] portal: invite client_id profile sync failed:', invErr);
                }
              }
            } catch (e) {
              console.warn('Failed to check portal invites', e);
            }
          }
        }

        // Hoofd-app: CLIENT / CLIENT_RECRUITER — uitnodiging, dan CRM-contact
        if ((profile.role === 'CLIENT' || profile.role === 'CLIENT_RECRUITER') && em && !isClientPortalDomain) {
          try {
            const { data: invite } = await supabase
              .from('client_portal_invites')
              .select('client_id')
              .ilike('email', em)
              .maybeSingle();
            if (invite && invite.client_id) {
              effectiveClientId = invite.client_id;
              if (profile.client_id !== invite.client_id) {
                await supabase.from('profiles').update({ client_id: invite.client_id }).eq('id', uid);
              }
            }
          } catch (e) {
            console.warn('Failed to check portal invites', e);
          }
          if (!effectiveClientId) {
            const crmId = await tryResolveClientFromCrmContact();
            if (crmId) {
              effectiveClientId = crmId;
              if (profile.client_id !== crmId) {
                await supabase.from('profiles').update({ client_id: crmId }).eq('id', uid);
              }
            }
          }
        }

        // 2. Fetch Client Details if needed (Manual Join)
        let effectiveClientLogoUrl: string | undefined;
        let portalFullPipelineVisibility = true;
        if (effectiveClientId) {
          try {
            const { data: clientData } = await supabase
              .from('clients')
              .select('name, logo_url, clientportal_custom_domain')
              .eq('id', effectiveClientId)
              .maybeSingle();
            if (clientData) {
              effectiveClientName = clientData.name;
              const lu = String((clientData as any).logo_url || '').trim();
              if (lu) effectiveClientLogoUrl = lu;
              portalFullPipelineVisibility = resolvePortalFullPipelineVisibility(clientData as any);
            }
          } catch (e) { console.warn("Could not fetch client name", e); }
        }

        const visibleTabs = resolveVisibleTabsFromProfile(profile, {
            isClientPortal: isClientPortalDomain,
        });

        const profileData =
          profile.data && typeof profile.data === 'object' && !Array.isArray(profile.data)
            ? (profile.data as Record<string, unknown>)
            : {};
        const rawSidebarHidden = profileData.sidebar_hidden_ids;
        const sidebarHiddenIds = Array.isArray(rawSidebarHidden)
          ? rawSidebarHidden.filter((x): x is string => typeof x === 'string')
          : [];
        const rawOrder = profileData.sidebar_order_ids;
        const sidebarOrderIds = Array.isArray(rawOrder)
          ? rawOrder.filter((x): x is string => typeof x === 'string')
          : undefined;

        const userData: User = {
          id: profile.id,
          name: profile.name,
          role: profile.role as UserRole,
          email: em || profile.email,
          phone: profile.phone || '',
          credits: profile.credits || 0,
          avatar: profile.photo_url || profile.avatar_url || profile.avatar,
          clientId: effectiveClientId,
          clientName: effectiveClientName,
          clientLogoUrl: isClientPortalDomain ? effectiveClientLogoUrl : undefined,
          portalFullPipelineVisibility,
          visibleTabs: visibleTabs,
          sidebarHiddenIds,
          sidebarOrderIds: sidebarOrderIds?.length ? sidebarOrderIds : undefined,
          tokensInput: profile.tokens_used_input || 0,
          tokensOutput: profile.tokens_used_output || 0
        };

        // Fetch Branding if client exists
        if (effectiveClientId) {
          console.log("[App] fetching branding for", effectiveClientId);
          try {
            // Re-use the existing helper which correctly parses the 'config' JSON column
            const brandingConfig = await supabase_get_client_branding(effectiveClientId);
            if (brandingConfig) {
              userData.branding = brandingConfig;
            } else {
              // Fallback logic for legacy systems that used dedicated columns, just in case
              const fallback = await supabase.from('client_branding').select('*').eq('client_id', effectiveClientId).maybeSingle();
              if (fallback.data) {
                userData.branding = {
                  logoUrl: fallback.data.logo_url,
                  primaryColor: fallback.data.primary_color,
                  secondaryColor: fallback.data.secondary_color
                };
              }
            }
          } catch (brandingErr) { console.warn("Branding fetch fail", brandingErr); }
        }



        setUser(userData);
        setShowLogin(false);
      } else {
        console.error("Auth valid but profile missing or timed out. User ID:", uid);
        alert("Oeps! Je bent succesvol ingelogd, maar je gebruikersprofiel kon niet worden gevonden. Je accountgegevens zijn incompleet. Neem contact op met Werftools Support om je profiel te herstellen.");
        await supabase_safe_sign_out();
        setShowLogin(true);
      }
    } catch (err) {
      console.error("Profile fetch CRITICAL error", err);
      // Graceful degradation: Stop spinner, let user see they are on login screen or retry
      alert("Er is een fout opgetreden bij het laden van uw profiel. Probeer de pagina te vernieuwen.");
      await supabase_safe_sign_out();
      setShowLogin(true);
    } finally {
      console.log("[App] fetchProfile finally block - stop processing");
      isFetchingProfile.current = false;
      setIsProcessing(false);
      setIsLongLoading(false);
    }
  };

  const saveSidebarPrefs = useCallback(
    async (prefs: { hiddenIds: string[]; orderIds: string[] }) => {
      if (!user?.id || String(user.id).startsWith('impersonated-')) return;
      try {
        const { data: row } = await supabase.from('profiles').select('*').eq('id', user.id).maybeSingle();
        const prev =
          row?.data && typeof row.data === 'object' && !Array.isArray(row.data)
            ? (row.data as Record<string, unknown>)
            : {};
        const nextData = {
          ...prev,
          sidebar_hidden_ids: prefs.hiddenIds,
          sidebar_order_ids: prefs.orderIds,
        };
        const { error } = await supabase.from('profiles').update({ data: nextData }).eq('id', user.id);
        if (error) throw error;
        setUser((u) =>
          u
            ? {
                ...u,
                sidebarHiddenIds: prefs.hiddenIds,
                sidebarOrderIds: prefs.orderIds.length ? prefs.orderIds : undefined,
              }
            : null
        );
      } catch (e) {
        console.error('[App] saveSidebarPrefs', e);
        window.dispatchEvent(
          new CustomEvent('wt:toast', {
            detail: {
              message:
                'Apps-voorkeuren opslaan mislukt. Voer in Supabase uit: ALTER TABLE public.profiles ADD COLUMN IF NOT EXISTS data JSONB DEFAULT \'{}\'::jsonb;',
              type: 'error',
            },
          })
        );
      }
    },
    [user?.id]
  );

  useEffect(() => {
    if (!user?.sidebarHiddenIds?.length) return;
    const gid = sidebarGroupIdForTab(activeTab);
    if (user.sidebarHiddenIds.includes(gid)) {
      setActiveTabRaw(isClientPortalDomain ? 'engagement_center' : 'dashboard');
    }
  }, [activeTab, user?.sidebarHiddenIds, isClientPortalDomain]);

  const handleLogout = async () => {
    setUser(null);
    setActiveTab(isClientPortalDomain ? 'engagement_center' : 'dashboard');
    setSelectedVacancy(null);
    setIsCmsActive(false);
    await supabase.auth.signOut();
  };

  const handleLoginAsClient = async (clientId: string, clientName: string, portalTestEmail?: string) => {
    try {
      setIsProcessing(true);
      const envUrl = ((import.meta as any).env?.VITE_CLIENT_PORTAL_URL as string | undefined)?.trim();
      let origin = '';
      if (envUrl && /^https?:\/\//i.test(envUrl)) {
        origin = envUrl.replace(/\/$/, '');
      } else {
        const { protocol, hostname, port } = window.location;
        const p = port ? `:${port}` : '';
        if (hostname.toLowerCase().includes('clientportal')) {
          origin = `${protocol}//${hostname}${p}`;
        } else if (hostname === 'localhost' || hostname.endsWith('.localhost')) {
          origin = `${protocol}//clientportal.localhost${p}`;
        } else {
          origin = 'https://clientportal.werftools.nl';
        }
      }
      const emailQ =
        portalTestEmail && portalTestEmail.includes('@')
          ? `?portalEmail=${encodeURIComponent(portalTestEmail.trim())}`
          : '';
      window.open(`${origin}/${emailQ}`, '_blank', 'noopener,noreferrer');
      window.dispatchEvent(
        new CustomEvent('wt:toast', {
          detail: {
            message: `Clientportaal geopend voor ${clientName}. Log daar in met de magische link / code van het contact-e-mailadres.`,
            type: 'info',
          },
        })
      );
    } catch (e) {
      console.error(e);
      alert('Kon het clientportaal niet openen.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Removed handleImpersonate logic


  // --- RENDER PUBLIC SITE OVERLAY ---
  if (publicClientData && !isClientPortalDomain) {
    return (
      <PublicVacancyView
        clientId={publicClientData.client.id}
        customBranding={publicClientData.branding}
        propClient={publicClientData.client}
        onBack={() => {
          window.history.pushState(null, '', '/');
          setPublicClientData(null);
        }}
      />
    );
  }

  if (!user || isCmsActive) {
    if (isProcessing && !showLogin) {
      return (
        <div className="min-h-screen bg-[#F8FAFC] flex flex-col items-center justify-center animate-fadeIn p-4">
          <Loader2 className="animate-spin text-[#E21E26] mb-4" size={48} />
          <p className="text-xs font-black uppercase tracking-widest text-slate-400">Loading profile...</p>

          {isLongLoading && (
            <div className="mt-8 flex flex-col items-center gap-3 animate-slideUp">
              <div className="flex items-center gap-2 text-amber-600 text-xs font-bold bg-amber-50 px-4 py-2 rounded-lg border border-amber-100">
                <AlertCircle size={14} /> Connection slow
              </div>
              <button
                onClick={() => { setIsProcessing(false); setShowLogin(true); }}
                className="text-xs font-bold text-slate-500 hover:text-slate-900 underline cursor-pointer"
              >
                Cancel & Return to Login
              </button>
            </div>
          )}
        </div>
      );
    }

    if (isCmsActive && user?.role === UserRole.ADMIN) {
      return <WebsiteEditor user={user} vacancies={liveVacancies} onClose={() => { setIsCmsActive(false); supabase_get_site_config().then(c => setSiteConfig(c)); }} />;
    }

    if (isClientPortalDomain) {
       return (
         <div className="relative animate-in slide-in-from-right duration-300">
           <Login isClientPortal={true} />
         </div>
       );
    }

    return (
      <div className="relative animate-in slide-in-from-right duration-300">
        <Login />
      </div>
    );
  }



  // --- RENDER WAITING ROOM FOR ORPHANED USERS ---
  if (user && !user.clientId && user.role !== UserRole.ADMIN) {
    return (
      <PendingApproval 
        userName={user.name} 
        userEmail={user.email} 
        onLogout={handleLogout} 
      />
    );
  }

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        if (user.role === UserRole.ADMIN) return <AdminDashboard />;
        if (user.role === UserRole.CLIENT) return <ClientDashboard user={user} vacancies={liveVacancies} candidates={liveCandidates} />;
        return <Dashboard user={user} vacancies={liveVacancies} candidates={liveCandidates} />;
      case 'pipeline':
        return <GlobalPipeline user={user} vacancies={liveVacancies} isLoading={isLoadingData !== false} portalAllowedClientIds={isClientPortalDomain ? portalAllowedClientIds : undefined} onCandidateSelect={(id) => { setActiveTab('talentpool'); setLastTalentPoolCandidateId(id); }} />;
      case 'engagement_center':
        if (isClientPortalDomain) {
          return <ClientEngagementCenter user={user} />;
        }
        return (
          <CandidateEngagementCenter
            user={user}
            vacancies={liveVacancies}
            candidates={liveCandidates}
            onCockpitLayoutMode={onEngagementCockpitLayout}
            onStartCall={(profile) => {
              setActiveCallCandidate(profile.candidate);
              setActiveCallProfileId(profile.id);
              setEditCallCommId(undefined);
              setEditCallContent(undefined);
              setShowCallModal(true);
            }}
            onRefreshPool={() => refreshTalentPool()}
          />
        );
      case 'shortlist':
        return <GlobalShortlistTable 
          vacancies={liveVacancies}
          user={user}
          isLoading={isLoadingData !== false}
          portalAllowedClientIds={isClientPortalDomain ? portalAllowedClientIds : undefined}
          onCandidateSelect={(id) => { setActiveTab('talentpool'); setLastTalentPoolCandidateId(id); }}
          onVacancySelect={async (vid) => {
            let vac = liveVacancies.find(v => v.id === vid);
            if (!vac) {
               // Fetch missing vacancy locally to allow routing
               const { data } = await supabase.from('vacancies').select('*, client:clients(*), eindklant:clients!vacancies_eindklant_id_fkey(*), opdrachtgever:clients!vacancies_opdrachtgever_id_fkey(*)').eq('id', vid).single();
               if (data) vac = data;
            }
            if (vac) {
              setSelectedVacancy(vac);
              setActiveTab('vacancies');
            }
          }} 
        />;
      case 'workspace': return (
        <RecruiterWorkspace
          vacancies={liveVacancies}
          onNavigate={(targetTab, data) => {
            if (targetTab === 'talentpool') {
              setActiveTab('talentpool');
              // Future: Pass data.vacancyId to filter talent pool
            } else if (targetTab === 'vacancies') {
              if (typeof data === 'string') {
                const vac = liveVacancies.find(v => v.id === data);
                if (vac) {
                  setSelectedVacancy(vac);
                  setActiveTab('vacancies');
                }
              }
            } else {
              setActiveTab(targetTab as any);
            }
          }}
        />
      );
      case 'users': return <UserManagement user={user} />;
      case 'vacancy_templates': return <VacancyTemplates user={user} />;
      case 'vacancies':
        if (selectedVacancy) {
          return (
            <VacancyDashboard
              vacancy={selectedVacancy}
              onBack={() => {
                setSelectedVacancy(null);
                setIsEditingVacancy(false);
              }}
              user={user as any}
              initialTab={(selectedVacancy as any).initialStep || 'Vacature'}
              onVacancyUpdate={(updated) => {
                setSelectedVacancy(updated);
                setLiveVacancies(prev => prev.map(v => v.id === updated.id ? updated : v));
              }}
            />
          );
        } else {
          return (
            <VacancyList
              onDelete={(idOrIds) => {
                if (!idOrIds) return;
                const ids = Array.isArray(idOrIds) ? idOrIds : [idOrIds];
                setLiveVacancies(prev => prev.filter(v => !ids.includes(v.id)));
              }}
              onSelect={async (vac, step, isNew) => { 
                if ((vac as any)._isLight) {
                    setIsLoadingData(true);
                    try {
                        const fullVac = await supabase_get_vacancy_by_id(vac.id);
                        if (fullVac) {
                            const formattedFullVac = formatVacancyRowFromSupabase(fullVac as Record<string, unknown>);
                            setSelectedVacancy({ ...formattedFullVac, initialStep: step } as Vacancy);
                        } else {
                            setSelectedVacancy({ ...vac, initialStep: step } as any);
                        }
                    } catch(e) {
                         setSelectedVacancy({ ...vac, initialStep: step } as any);
                    } finally {
                        setIsLoadingData(false);
                        setIsEditingVacancy(!!isNew);
                    }
                } else {
                    setSelectedVacancy({ ...vac, initialStep: step } as any); 
                    setIsEditingVacancy(!!isNew); 
                }
              }}
              onUpdate={(updatedVacancy) => {
                setLiveVacancies(prev => prev.map(v => v.id === updatedVacancy.id ? updatedVacancy : v));
              }}
              user={user}
              vacancies={liveVacancies}
              clients={liveClients}
              candidates={liveCandidates}
              isLoading={isLoadingData}
              portalAllowedClientIds={isClientPortalDomain ? portalAllowedClientIds : undefined}
              onCreate={() => {
                 setCreateVacancyInitialData(null);
                 setActiveTab('create_vacancy');
              }}
            />
          );
        }
      case 'create_vacancy':
        return (
          <CreateVacancy
            user={user}
            initialData={createVacancyInitialData}
            onBack={() => {
               setActiveTab('vacancies');
               setCreateVacancyInitialData(null);
            }}
            onSave={(id, isDraft) => {
              setCreateVacancyInitialData(null);
              if (isDraft) {
                setActiveTab('vacancies');
              } else {
                // Go to flow
                // We need to set selectedVacancy first.
                // Since we just created it, it might not be in liveVacancies yet (async).
                // But we have the ID. We can try to fetch it or rely on live sync (which might race).
                // For now, let's filter from liveVacancies or just set ID if VacancyFlow supports fetching?
                // VacancyFlow takes 'vacancy' object.
                // We should probably rely on onNavigate with ID strategy like RecruiterWorkspace.
                // But App.tsx logic for 'vacancies' expects `selectedVacancy` object.
                // Let's rely on live sync finding it, or fetch it specifically.
                // Ideally we set activeTab='vacancies' and handle selection.
                // Let's try to set it if found, else wait?
                refreshVacancies(); // Force refresh
                // Simple approach: go to vacancies list for now, user can click. 
                // BETTER: Fetch it and set it.
                supabase_get_vacancies().then(all => {
                  const found = all?.find((v: any) => v.id === id);
                  if (found) {
                    // Need to format it same as refreshVacancies
                    const formatted = {
                      ...found,
                      clientId: found.client_id,
                      createdAt: new Date(found.created_at).toLocaleDateString('nl-NL'),
                      client: found.clients,
                      data: { ...found.data, tags: found.data?.tags || [], category: found.data?.category || '' }
                    };
                    setSelectedVacancy(formatted);
                    setActiveTab('vacancies');
                  } else {
                    setActiveTab('vacancies');
                  }
                });
              }
            }}
          />
        );
      case 'cv_builder':
        return (
          <CVBuilder
            key={`cv-${builderSessionId}`}
            user={user}
            initialData={cvBuilderData}
            initialTemplate={cvBuilderTemplate}
            portalProposalAttachMode={Boolean(cvBuilderReturn?.portalProposal)}
            onAttachPortalProposal={(snap) => {
              try {
                const ser = JSON.stringify(snap);
                if (ser.length > 4_500_000) {
                  setToastData({
                    message: 'CV-data te groot om te koppelen; verklein afbeeldingen of het aantal pagina’s.',
                    type: 'error',
                  });
                  return;
                }
                sessionStorage.setItem('wt_portal_proposal_cv', ser);
                window.dispatchEvent(new CustomEvent('wt:portal-proposal-cv-saved'));
                setToastData({
                  message: 'CV gekoppeld aan het voorstel. Ga terug naar de vacature om verder te gaan.',
                  type: 'success',
                  sticky: true,
                });
              } catch {
                setToastData({ message: 'Kon CV niet koppelen.', type: 'error' });
              }
            }}
            onBack={() => {
              const ret = cvBuilderReturn;
              setCvBuilderReturn(null);
              setCvBuilderData(null);
              setCvBuilderTemplate(undefined);
              if (ret?.tab === 'vacancies' && ret.vacancyId && liveVacancies?.length) {
                const vac = liveVacancies.find((v) => v.id === ret.vacancyId);
                if (vac) setSelectedVacancy(vac as any);
              }
              setActiveTab(ret?.tab || 'talentpool');
            }}
          />
        );
      case 'cv_templates':
        return <CVTemplateManager
          user={user}
          onBack={() => setActiveTab('dashboard')}
          onNavigate={(tab, docContent) => {
            if (tab === 'docs' && docContent) {
              setDocsData({
                title: docContent.title,
                content: docContent.content || '',
                candidateId: docContent.candidateId,
                fileId: docContent.initialFileId
              } as any);
            }
            if (tab === 'documentbuilder' && docContent) {
              setDocumentBuilderData(docContent);
            }
            setActiveTab(tab);
          }}
        />;
      case 'docs':
        return <DocsManager
          user={user}
          initialData={docsData}
          initialFileId={docsData?.fileId} // Pass fileId if present
          onClearData={() => setDocsData(null)}
          onBack={() => {
            if (lastTalentPoolCandidateId) {
              setActiveTab('talentpool');
            } else {
              setActiveTab('dashboard'); // Fallback
            }
            setDocsData(null);
          }}
          candidateId={lastTalentPoolCandidateId}
          onOpenDocumentBuilder={(docContent) => {
              setDocumentBuilderData(docContent);
              setActiveTab('documentbuilder');
          }}
          key="docs-manager"
        />;
    case 'documentbuilder':
      return <DocumentBuilder 
          user={user} 
          initialData={documentBuilderData}
          initialFileId={documentBuilderData?.fileId}
          portalProposalAttachMode={Boolean(documentBuilderReturn?.portalProposal)}
          onAttachPortalProposal={(snap) => {
            try {
              const ser = JSON.stringify(snap);
              if (ser.length > 4_500_000) {
                setToastData({
                  message: 'CV-data te groot om te koppelen; verklein het document of verwijder grote blokken.',
                  type: 'error',
                });
                return;
              }
              sessionStorage.setItem('wt_portal_proposal_cv', ser);
              window.dispatchEvent(new CustomEvent('wt:portal-proposal-cv-saved'));
              setToastData({
                message: 'Document gekoppeld aan het voorstel. Ga terug naar de vacature om verder te gaan.',
                type: 'success',
                sticky: true,
              });
            } catch {
              setToastData({ message: 'Kon document niet koppelen.', type: 'error' });
            }
          }}
          onBack={() => {
              const ret = documentBuilderReturn;
              setDocumentBuilderReturn(null);
              setDocumentBuilderData(null);
              if (ret?.portalProposal && ret.vacancyId && ret.applicationId) {
                void (async () => {
                  try {
                    const all = await supabase_get_vacancies();
                    const found = all?.find((v: any) => v.id === ret.vacancyId);
                    if (found) {
                      const formatted = {
                        ...found,
                        clientId: found.client_id,
                        createdAt: new Date(found.created_at).toLocaleDateString('nl-NL'),
                        client: found.clients,
                        data: { ...found.data, tags: found.data?.tags || [], category: found.data?.category || '' }
                      };
                      setSelectedVacancy(formatted as any);
                    }
                  } catch { /* ignore */ }
                  setActiveTab('vacancies');
                  window.setTimeout(() => {
                    window.dispatchEvent(
                      new CustomEvent(RESUME_VOORGESTELD_MOVE_MODAL_EVENT, {
                        detail: { vacancyId: ret.vacancyId, applicationId: ret.applicationId },
                      })
                    );
                  }, 180);
                })();
                return;
              }
              if (lastTalentPoolCandidateId) {
                  setActiveTab('talentpool');
              } else {
                  setActiveTab('dashboard');
              }
          }} 
      />;
      case 'doc_shortcodes':
      return <DocumentShortcodes />;
      case 'clients': return <ClientsManager user={user as any} onLoginAs={handleLoginAsClient} initialClientId={lastClientId} />;
      case 'crm': return <CRMManager user={user as any} initialEntityId={lastCrmEntityId} onLoginAs={handleLoginAsClient} onNavigateVacancy={(id) => {
          const vac = liveVacancies.find(v => v.id === id);
          if (vac) {
              setSelectedVacancy(vac);
              setActiveTab('vacancies');
          }
      }} onNavigateCandidate={(id) => {
          setLastTalentPoolCandidateId(id);
          setActiveTab('talentpool');
      }} onNavigateToShortlist={() => setActiveTab('shortlist')} onNavigateToVacancies={() => {
          setSelectedVacancy(null);
          setActiveTab('vacancies');
      }} />;
      case 'careers_site': return <CareersSiteEditor user={user} />;
      case 'placements': return <PlacementsList user={user} />;
      case 'talentpool': return (
        <TalentPool
          user={user}
          candidates={liveCandidates}
          vacancies={liveVacancies}
          isLoading={isLoadingData}
          initialCandidateId={lastTalentPoolCandidateId}
          onStartCall={(profile) => {
            setActiveCallCandidate(profile.candidate);
            setActiveCallProfileId(profile.id);
            setEditCallCommId(undefined);
            setEditCallContent(undefined);
            setShowCallModal(true);
          }}
          onEditCallNote={(profile, commId, content) => {
            setActiveCallCandidate(profile.candidate);
            setActiveCallProfileId(profile.id);
            setEditCallCommId(commId);
            setEditCallContent(content);
            setShowCallModal(true);
          }}
          onNavigate={(tab, data: any, template, docContent) => {
              console.log('ON NAVIGATE CALLED!', tab, data);
              if (tab === 'vacancies') {
                const targetId = typeof data === 'string' ? data : data?.id;

                if (!targetId) {
                  setActiveTab('vacancies');
                  return;
                }

                const vac = liveVacancies.find(v => v.id === targetId);
                if (vac) {
                  const finalVac = typeof data === 'object' ? { ...vac, ...data } : vac;
                  setSelectedVacancy(finalVac as any);
                  setActiveTab('vacancies');
                } else {
                  // Fallback: Fetch directly from Supabase if not in liveVacancies
                  supabase.from('vacancies').select('*, clients!vacancies_client_id_fkey(*), eindklant:clients!vacancies_eindklant_id_fkey(*)').eq('id', targetId).single().then(({ data: fetchedVac }) => {
                    if (fetchedVac) {
                      const formatted = {
                        ...fetchedVac,
                        clientId: fetchedVac.client_id,
                        createdAt: new Date(fetchedVac.created_at).toLocaleDateString('nl-NL'),
                        client: fetchedVac.clients,
                        data: { ...fetchedVac.data, tags: fetchedVac.data?.tags || [], category: fetchedVac.data?.category || '' }
                      };
                      const finalVac = typeof data === 'object' ? { ...formatted, ...data } : formatted;
                      setSelectedVacancy(finalVac as any);
                    }
                    setActiveTab('vacancies');
                  });
                }
                return;
              }
              // If navigating to docs, persist the candidate ID
              if (tab === 'docs' && docContent && docContent.candidateId) {
                setLastTalentPoolCandidateId(docContent.candidateId);
              }

              setActiveTab(tab);
              if (tab === 'cv_builder') {
                setCvBuilderData(data as any);
                setCvBuilderTemplate(template);
                setCvBuilderReturn(null);
                setBuilderSessionId(Date.now());
              }
              if (tab === 'docs' && docContent) {
                setDocsData({
                  title: docContent.title,
                  content: docContent.content,
                  candidateId: docContent.candidateId,
                  fileId: (docContent as any).fileId // Cast to allow new property
                } as any);
              }
              if (tab === 'documentbuilder' && docContent) {
                setDocumentBuilderData({
                  title: docContent.title,
                  content: docContent.content,
                  candidateId: docContent.candidateId,
                  fileId: (docContent as any).fileId
                } as any);
              }
            }}
        />
      );
      case 'reporting':
        return isClientPortalDomain ? (
          <ClientPortalReportingComingSoon user={user} />
        ) : (
          <Reporting
            vacancies={liveVacancies}
            candidates={liveCandidates}
            clients={liveClients}
            user={user}
          />
        );
      case 'reporting_kpi': return <ReportingKpiSettings user={user} clients={liveClients} />;
      case 'reporting_ratios': return <ReportingRatios user={user} />;
      case 'automation': return <AutomationManager user={user} />;
      case 'subscription': return <SubscriptionManager user={user} />;
      case 'logs': return <LogsManager />;
      case 'products': return <ProductCatalog />;
      case 'sourcing': return <SourcingTool user={user} />;

      case 'settings': return <Settings user={user} onLogout={handleLogout} />;
      case 'bullhorn_pipeline': return <BullhornPipelineSync user={user} />;
      case 'account_success': return <AccountSuccess onContinue={() => setActiveTab(isClientPortalDomain ? 'engagement_center' : 'dashboard')} />;
      default: return null;
    }
  };

  const LayoutComponent = isClientPortalDomain ? ClientPortalLayout : Layout;

  return (
    <>
      {toastData && (
        <div
          className={`fixed top-4 left-1/2 z-[9999] flex max-w-[min(96vw,640px)] -translate-x-1/2 items-center gap-3 rounded-2xl px-5 py-3 shadow-2xl animate-in slide-in-from-top-4 fade-in duration-300 ${
            toastData.type === 'error'
              ? 'border border-red-100 bg-red-50 text-red-600'
              : toastData.type === 'success'
                ? 'border border-green-100 bg-green-50 text-green-700'
                : 'bg-slate-900 text-white'
          }`}
        >
          <AlertCircle size={16} className={toastData.type === 'error' ? 'text-red-500' : 'shrink-0 text-slate-400'} />
          <span className="min-w-0 flex-1 text-[11px] font-black uppercase tracking-widest leading-snug">{toastData.message}</span>
          {toastData.sticky ? (
            <button
              type="button"
              onClick={() => setToastData(null)}
              className="shrink-0 rounded-lg p-1.5 text-slate-500 transition hover:bg-black/5 hover:text-slate-800"
              aria-label="Melding sluiten"
            >
              <X size={16} />
            </button>
          ) : null}
        </div>
      )}
      <LayoutComponent
      user={user}
      onLogout={handleLogout}
      activeTab={activeTab}
      engagementCockpitLayout={engagementCockpitLayout}
      setActiveTab={(tab) => { setActiveTab(tab); setSelectedVacancy(null); setIsEditingVacancy(false); }}
      contentLoading={isClientPortalDomain && isLoadingData !== false}
      onSaveSidebarPrefs={saveSidebarPrefs}
      onOpenChat={() => setShowFroukjeChat(true)}
      taskbarItems={taskbarItems}
      activeTaskbarId={activeTaskbarId}
      onTaskbarClick={handleTaskbarClick}
      onRemoveTaskbarItem={removeTaskbarItem}
    >
      <div className="fixed bottom-4 left-4 z-[500] pointer-events-none hidden md:flex flex-col gap-2 items-start">


        {isLiveConnected && (
          <div className="bg-emerald-500/10 backdrop-blur-md border border-emerald-500/20 text-emerald-600 px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest flex items-center gap-2 shadow-sm animate-fadeIn">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            Live
          </div>
        )}
        {isLoadingData && (
          <div className="bg-red-0/10 backdrop-blur-md border border-red-0/20 text-red- px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest flex items-center gap-2 shadow-sm animate-fadeIn">
            <Loader2 size={10} className="animate-spin" /> Syncing...
          </div>
        )}
        {!isLiveConnected && !isLoadingData && user?.id !== 'dev-user' && (
          <button onClick={() => refreshAllData()} className="pointer-events-auto bg-red-500/10 backdrop-blur-md border border-red-500/20 text-red-600 px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest flex items-center gap-2 shadow-sm animate-fadeIn hover:bg-red-500/20 transition-all">
            <RefreshCw size={10} /> Retry
          </button>
        )}
      </div>
      <div className={`h-full transition-all duration-300 ${showFroukjeChat ? 'mr-[450px]' : ''} ${showCallModal ? 'mr-[320px]' : ''}`}>
        {renderContent()}
      </div>
      {user && (
        <TodoWidget
          userId={user.id}
          clientId={user.clientId}
          vacancyId={selectedVacancy?.id}
          isOpen={showTodoWidget}
          onToggle={() => setShowTodoWidget(!showTodoWidget)}
        />
      )}
      <AskFroukjeChat
        isOpen={showFroukjeChat}
        onClose={() => setShowFroukjeChat(false)}
        vacancies={liveVacancies}
        candidates={liveCandidates}
        aiRecruiter={user?.branding?.aiRecruiter}
        user={user}
        onNavigateToCandidate={(id) => {
          setLastTalentPoolCandidateId(id);
          setActiveTab('talentpool');
          setShowFroukjeChat(false);
        }}
      />

      {showCallModal && activeCallCandidate && activeCallProfileId && (
        <CallSidebar
          isOpen={showCallModal}
          onClose={() => {
            setShowCallModal(false);
            setEditCallCommId(undefined);
            setEditCallContent(undefined);
          }}
          candidate={activeCallCandidate}
          profileId={activeCallProfileId}
          currentUser={user}
          editCommId={editCallCommId}
          initialContent={editCallContent}
          onCommunicationSaved={() => {
            refreshTalentPool();
          }}
        />
      )}

      <GlobalLoader />
      
      {/* Global Taskbar is now rendered in Layout header */}
    </LayoutComponent>
    </>
  );
};


export default App;
// Force Rebuild
